<template>
  <div>
    <BaseAlert
      :color="result?.fiskalyTaxpayer?.id ? 'primary' : 'warning'"
      :text="
        result?.fiskalyTaxpayer?.id
          ? $t('tax_payer_entity.info-alert.text')
          : $t('tax_payer_entity.alert.text')
      "
      :primaryAction="
        result?.fiskalyTaxpayer?.id
          ? $t('tax_payer_entity.info-alert.action')
          : $t('tax_payer_entity.alert.action')
      "
      @primaryAction="showTaxPayerEntityModal = true"
      v-test="
        `company-settings-tax-info${result?.fiskalyTaxpayer?.id ? '' : '-not'}-set`
      "
    />
    <BaseModal
      v-if="showTaxPayerEntityModal"
      useForm
      :heading="$t('tax_payer_entity.title')"
      testId="tax-payer-entity-modal"
      @close="closeModal"
      @submit="handleSubmit"
    >
      <Flex direction="column">
        <BaseText>{{ $t('tax_payer_entity.info') }}</BaseText>
        <BaseGrid container>
          <BaseGrid :size="4">
            <BaseInput
              v-model="formData.taxNumber"
              :minLength="13"
              :maxLength="13"
              :regex="DE_TAX_NUMBER_VALIDATION_REGEX"
              :label="$t('tax_payer_entity.form.tax_number')"
              required
              v-test="'company-settings-tax-info-tax-number'"
            />
          </BaseGrid>
          <BaseGrid :size="4">
            <BaseInput
              v-model="formData.taxOfficeNumber"
              :minLength="4"
              :maxLength="4"
              :regex="DE_TAX_OFFICE_NUMBER_VALIDATION_REGEX"
              :label="$t('tax_payer_entity.form.tax_office_number')"
              required
              v-test="'company-settings-tax-info-tax-office-number'"
            />
          </BaseGrid>
          <BaseGrid :size="4">
            <BaseInput
              v-model="formData.vatNumber"
              :regex="DE_VAT_NUMBER_VALIDATION_REGEX"
              placeholder="DE123456789"
              :label="$t('tax_payer_entity.form.vat_number')"
              required
              v-test="'company-settings-tax-info-vat-number'"
            />
          </BaseGrid>
        </BaseGrid>
        <BaseDropdown
          v-model="formData.personType"
          :label="$t('tax_payer_entity.form.person_type')"
          :options="personTypeOptions"
          v-test="'company-settings-tax-info-person-type'"
        />
        <div
          v-if="formData.personType === PersonType.Legal"
          v-test="'company-settings-person-type-legal-form'"
        >
          <Flex direction="column">
            <BaseInput
              v-model="formData.companyName"
              :label="$t('tax_payer_entity.form.company_name')"
              required
              v-test="'company-settings-tax-info-company-name'"
            />
            <BaseDropdown
              v-model="formData.legalForm"
              searchable
              :label="$t('tax_payer_entity.form.legal_form')"
              :options="germanLegalFormOptions"
              v-test="'company-settings-tax-info-legal-form'"
            />
          </Flex>
        </div>
        <Flex
          v-else
          direction="column"
          v-test="'company-settings-person-type-natural-form'"
        >
          <BaseGrid container>
            <BaseGrid :size="2">
              <BaseDropdown
                v-model="formData.salutation"
                :label="$t('global.salutation')"
                :options="GermanSalutationOptions"
                v-test="'company-settings-tax-info-salutation'"
              />
            </BaseGrid>
            <BaseGrid
              :size="4"
              :mSize="5"
            >
              <BaseInput
                v-model="formData.firstName"
                :label="$t('global.first_name')"
                required
                v-test="'company-settings-tax-info-first-name'"
              />
            </BaseGrid>
            <BaseGrid
              :size="4"
              :mSize="5"
            >
              <BaseInput
                v-model="formData.lastName"
                :label="$t('global.last_name')"
                required
                v-test="'company-settings-tax-info-last-name'"
              />
            </BaseGrid>
          </BaseGrid>
          <BaseInput
            v-model="formData.birthdate"
            type="date"
            :label="$t('global.birthday')"
            required
            v-test="'company-settings-tax-info-birthdate'"
          />
          <BaseInput
            v-model="formData.identificationNumber"
            :label="$t('global.identification_number')"
            required
            v-test="'company-settings-tax-info-identification-number'"
          />
        </Flex>
      </Flex>
      <template #footer>
        <BaseButton
          color="inverted"
          @click="closeModal"
        >
          {{ $t('global.actions.cancel') }}
        </BaseButton>
        <BaseButton
          submitForm
          v-test="'company-settings-tax-info-submit'"
        >
          {{ $t('global.actions.submit') }}
        </BaseButton>
      </template>
    </BaseModal>
  </div>
</template>

<script
  setup
  lang="ts"
>
import { ref, watch } from 'vue';
import BaseDropdown from '@/components/base-dropdown/index.vue';
import {
  DE_TAX_NUMBER_VALIDATION_REGEX,
  DE_TAX_OFFICE_NUMBER_VALIDATION_REGEX,
  DE_VAT_NUMBER_VALIDATION_REGEX,
} from '@/helpers/regex';
import { useGermanTaxPayerEntityOptions } from './taxPayerEntityOptions';
import { GermanLegalForms, PersonType } from './types';

const { personTypeOptions, germanLegalFormOptions, GermanSalutationOptions } =
  useGermanTaxPayerEntityOptions();

import { useMutation, useQuery } from '@vue/apollo-composable';
import { useI18n } from 'vue-i18n';
import { flash } from '@/helpers/ui';
import { useCompanyStore } from '@/stores/company';
import { GET_FISKALY_TAXPAYER, UPSERT_FISKALY_TAXPAYER } from './graphql';

const { t } = useI18n();

const { company } = useCompanyStore();

const { result, refetch } = useQuery(GET_FISKALY_TAXPAYER);

const showTaxPayerEntityModal = ref(false);

const formData = ref({
  taxNumber: '',
  taxOfficeNumber: '',
  vatNumber: '',
  companyName: company.name || '',
  personType: PersonType.Legal,
  legalForm: GermanLegalForms.Aktiengesellschaft,
  birthdate: null,
  firstName: '',
  lastName: '',
  identificationNumber: '',
  salutation: '1',
});

watch(
  () => result.value?.fiskalyTaxpayer,
  (taxpayer) => {
    if (taxpayer) {
      formData.value = {
        taxNumber:
          taxpayer.taxNumber || company.billingInfo?.stripeTaxId?.value || '',
        taxOfficeNumber: taxpayer.taxOfficeNumber || '',
        vatNumber: taxpayer.vatNumber || '',
        companyName: taxpayer.companyName || company.name || '',
        personType: taxpayer.personType || PersonType.Legal,
        legalForm: taxpayer.legalForm || GermanLegalForms.Aktiengesellschaft,
        birthdate: taxpayer.birthdate || null,
        firstName: taxpayer.firstName || '',
        lastName: taxpayer.lastName || '',
        identificationNumber: taxpayer.identificationNumber || '',
        salutation: taxpayer.salutation || '1',
      };
    }
  },
  { immediate: true },
);

const { mutate } = useMutation(UPSERT_FISKALY_TAXPAYER);

const handleSubmit = () => {
  const variables = {
    input: {
      taxNumber: formData.value.taxNumber,
      taxOfficeNumber: formData.value.taxOfficeNumber,
      vatNumber: formData.value.vatNumber,
      personType: formData.value.personType,
      companyName: formData.value.companyName || undefined,
      legalForm: formData.value.legalForm || undefined,
      birthdate: formData.value.birthdate || undefined,
      firstName: formData.value.firstName || undefined,
      lastName: formData.value.lastName || undefined,
      identificationNumber: formData.value.identificationNumber || undefined,
      salutation: formData.value.salutation || undefined,
    },
  };

  mutate(variables).then((response) => {
    if (response?.errors) {
      flash(t('global.errors.something_went_wrong'));
      return;
    }
    refetch();
    closeModal();
  });
};

const closeModal = () => {
  showTaxPayerEntityModal.value = false;
};
</script>
